import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { bodyCss, formSectionTitleCss } from '../../legacy-design-system/text'
import { AgentKeyType, Field } from './field'
import { css, cx } from '@emotion/css'
import { getAccountsRoute, getSuperadminAccountsRoute } from '../route'
import { OrganizationProps, SampleApiRequest } from './sample-api-request'
import { Dropdown } from '../../legacy-design-system/dropdown'
import { Role, RoleSwitch, SuperAdminRole } from '../roles'
import { SuperAdminRoleSwitch } from '../roles/role-switch'
import { AgentUrlField } from './agent-url-field'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { useSplitClient } from 'contrastBase/state'
import { Accordion } from '@contrast-security-inc/design-system-surfaces'
import { ApiKeysGrid } from './api-keys-grid'
import {
  ApiKeysResponse,
  useApiKeys
} from '../add-agent/download-agent/wizard/api'
import { RouteContext } from '../route-provider'
import { RenderRotateKey } from './rotate-keys'
import { getOrgUuid } from '../add-agent/download-agent/wizard/common/utils'

const keysColumnContainerCss = css`
  display: flex;
  flex-direction: column;
  max-width: 100%;
`

const itemSpaceCss = css`
  margin-bottom: 20px;
  display: block;
`

const footerCss = css`
  display: flex;
  margin-top: 10px;
`

const personalKeysCss = cx(
  bodyCss,
  css`
    align-items: center;
    display: flex;
  `
)

const btnContainerCss = css`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`

const dropdownCss = css`
  width: auto;
  min-width: 270px;
  min-width: 300px;
`

export interface ApiKeysParentProps {
  readonly apiKey?: string
  readonly contrastURL: string
  readonly authHeader: string
  readonly agentServiceKey?: string
  readonly agentUsername?: string
  readonly token?: string
  readonly onRotateAgentServiceKey: () => void
  readonly onRotateApiKey: () => void
  readonly onCopyAPIRequest: () => void
}

export const ApiKeysWithMultipleUsers = (props) => {
  const { isSplitTreatmentOn } = useSplitClient()
  const multipleUsersEnabled = isSplitTreatmentOn(
    'PROD-3155_multiple_agent_users'
  )

  const currentAgentId = getAgentId()

  return (
    <ApiKeys
      multipleUsersEnabled={multipleUsersEnabled}
      currentAgentId={currentAgentId}
      {...props}
    />
  )
}

export interface ApiKeysProps extends ApiKeysParentProps {
  readonly organization: OrganizationProps
  readonly renderOrganizationDropdown?: () => React.ReactNode
  readonly role?: Role
  /*verify if it should validate or not the provided "role" prop in order to show|hide elements in the view*/
  readonly checkRole: boolean
  readonly multipleUsersEnabled: boolean
  readonly currentAgentId?: string
}

const useOrgUuid = () => {
  const {
    parameters: [orgUuid]
  } = React.useContext(RouteContext)

  return {
    orgUuid
  }
}

export const getAgentId = (): string => location.hash.split('/')?.at(4) || ''

const accordionCss = css`
  margin-left: -8px;
  div {
    justify-content: unset;
  }
`

const ApiKeys: React.FC<ApiKeysProps> = (props) => {
  const {
    apiKey,
    organization,
    contrastURL,
    authHeader,
    onCopyAPIRequest,
    renderOrganizationDropdown,
    multipleUsersEnabled,
    checkRole,
    role,
    agentServiceKey,
    agentUsername,
    token,
    onRotateApiKey,
    onRotateAgentServiceKey,
    currentAgentId
  } = props

  const currentFirstname = currentAgentId?.match(/[@_][^@_]*$/)?.[0].slice(1)

  const renderAgentKeys = () => {
    const fields = (
      <>
        <Field
          label={{ id: 'title_agentServiceKey' }}
          value={data?.service_key ? data.service_key : agentServiceKey}
          inlineDescription
          description={RenderRotateKey(
            multipleUsersEnabled,
            onRotateAgentServiceKey,
            checkRole,
            role
          )}
          testId="agent-service-key"
          isObfuscated={multipleUsersEnabled}
          showObfuscationButton={multipleUsersEnabled}
          orgUuid={getOrgUuid()}
          agentUid={data?.user_uid}
          valueType={AgentKeyType.ServiceKey}
        />
        <Text font="metadata" color="neutral60" className={itemSpaceCss}>
          <FormattedMessage
            id="detail_learnHowToUseAgentServiceWithLink"
            values={{
              b: (...msg) => <b>{msg}</b>,
              a: (...msg) => (
                <Link href={getAccountsRoute(organization.id)} isExternal>
                  {msg}
                </Link>
              )
            }}
          />
        </Text>
        {!!apiKey && (
          <>
            <Field
              className={itemSpaceCss}
              label={{ id: 'title_apiKey' }}
              value={data?.api_key ? data.api_key : apiKey}
              inlineDescription
              description={RenderRotateKey(
                multipleUsersEnabled,
                onRotateApiKey,
                checkRole,
                role
              )}
              testId="api-key"
              isObfuscated={multipleUsersEnabled}
              showObfuscationButton={multipleUsersEnabled}
              orgUuid={getOrgUuid()}
              agentUid={data?.user_uid}
              valueType={AgentKeyType.ApiKey}
            />
            <AgentUrlField
              organizationUuid={organization.id}
              itemSpaceCss={itemSpaceCss}
            />
          </>
        )}
      </>
    )

    return (
      <div className={keysColumnContainerCss}>
        <FormattedMessage
          id={
            multipleUsersEnabled ? 'title_agentKeyFirstname' : 'title_agentKeys'
          }
          values={
            multipleUsersEnabled ? { firstname: currentFirstname } : undefined
          }
        >
          {(text) => (
            <div className={cx(itemSpaceCss, formSectionTitleCss)}>{text}</div>
          )}
        </FormattedMessage>
        <Field
          className={itemSpaceCss}
          label={{
            id: 'title_agentKeyName'
          }}
          value={data?.user_uid ? data?.user_uid : agentUsername}
          testId="agent-username"
          orgUuid={getOrgUuid()}
          agentUid={data?.user_uid}
          valueType={AgentKeyType.Username}
        />
        {
          <>
            <Field
              className={itemSpaceCss}
              label={{ id: 'title_agentToken' }}
              value={data?.token ? data.token : token}
              testId="agent-token"
              limitedCodeBlock
              isObfuscated={multipleUsersEnabled}
              showObfuscationButton={multipleUsersEnabled}
              orgUuid={getOrgUuid()}
              agentUid={data?.user_uid}
              valueType={AgentKeyType.Token}
            />
            <Accordion
              className={accordionCss}
              showBorder={false}
              testId="legacy-agent-keys"
              title={
                <Text font="bodyStrong" color="neutral60">
                  <FormattedMessage id="title_legacyAgentKeys" />
                </Text>
              }
              initialIsOpen={false}
            >
              {() => fields}
            </Accordion>
          </>
        }
      </div>
    )
  }

  const renderAgentInformation = () => {
    if (!organization.isSuperadmin && agentServiceKey && agentUsername) {
      return checkRole
        ? !!role && (
            <RoleSwitch
              role={role}
              requiredRole="EDIT"
              renderAllowed={() => renderAgentKeys()}
            />
          )
        : renderAgentKeys()
    }
    return null
  }

  const {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    data = {} as ApiKeysResponse,
    refetch = null,
    isLoading = false
  } = multipleUsersEnabled
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useApiKeys({
        // eslint-disable-next-line react-hooks/rules-of-hooks
        organizationUuid: useOrgUuid().orgUuid,
        agentUid: currentAgentId
      })
    : {}

  useEffect(() => {
    if (currentAgentId) {
      if (localStorage.getItem('selectedAgentId') !== currentAgentId) {
        localStorage.setItem('selectedAgentId', currentAgentId)
        if (refetch) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          refetch()
        }
      }
    }
  }, [refetch, currentAgentId])

  return !isLoading ? (
    <div>
      {!currentAgentId && multipleUsersEnabled ? (
        <ApiKeysGrid initialPageSize={10} role={role} checkRole={checkRole} />
      ) : (
        <>
          <div>{renderAgentInformation()}</div>
          <div className={footerCss}>
            <span className={personalKeysCss}>
              <FormattedMessage
                id="detail_apiKeys_navigateToYourAccount"
                values={{
                  a: (msg) => {
                    const link = renderOrganizationDropdown
                      ? getSuperadminAccountsRoute()
                      : getAccountsRoute(organization.id)
                    return (
                      <Link href={link} isExternal>
                        &nbsp;{msg}
                      </Link>
                    )
                  }
                }}
              />
            </span>
            {!!apiKey && !multipleUsersEnabled && (
              <span className={btnContainerCss}>
                <SampleApiRequest
                  apiKey={apiKey}
                  organization={organization}
                  contrastURL={contrastURL}
                  acceptHeader="application/json"
                  authHeader={authHeader}
                  onCopyAPIRequest={onCopyAPIRequest}
                />
              </span>
            )}
          </div>
        </>
      )}
    </div>
  ) : (
    <></>
  )
}

interface ApiKeysSuperadminProps extends ApiKeysParentProps {
  readonly organizations?: OrganizationProps[]
  readonly onSelectedOrg?: (orgID: string) => void
  readonly role: SuperAdminRole
}

export interface ApiKeysSuperadminState {
  readonly organization: OrganizationProps
}

export class ApiKeysSuperadmin extends React.Component<
  ApiKeysSuperadminProps,
  ApiKeysSuperadminState
> {
  public state: ApiKeysSuperadminState = {
    organization: {
      id: '',
      name: '',
      isSuperadmin: false
    }
  }

  public render() {
    const { organizations, role, ...rest } = this.props
    const { organization } = this.state

    if (!organizations || organizations.length === 0) {
      return false
    }

    const selectedOrganization =
      organizations.find((org) => org.id === organization.id) ||
      organizations[0]

    return (
      <SuperAdminRoleSwitch
        role={role}
        requiredRole={'SUPERADMIN'}
        renderAllowed={() => (
          <ApiKeys
            multipleUsersEnabled={false}
            {...rest}
            checkRole={false}
            organization={selectedOrganization}
            renderOrganizationDropdown={() => {
              return (
                organizations.length > 1 && (
                  <Field
                    className={itemSpaceCss}
                    label={{
                      id: 'title_organization'
                    }}
                    testId="organization-dropdown"
                    description={
                      <Dropdown
                        selectedItem={selectedOrganization}
                        items={organizations}
                        itemToString={(org) => org.name}
                        itemToKey={(org) => org.id}
                        onChange={this.onSelectOrg}
                        styleOverrides={{
                          outerContainerCss: dropdownCss
                        }}
                      />
                    }
                  />
                )
              )
            }}
          />
        )}
      />
    )
  }

  private onSelectOrg = (org: OrganizationProps) => {
    const { onSelectedOrg } = this.props

    this.setState(
      {
        organization: org
      },
      () => {
        if (!!onSelectedOrg) {
          onSelectedOrg(org.id)
        }
      }
    )
  }
}
